/*
  ЦИФРОВОЙ ДИАК ЗФ — KANBAN BACKLOG ATTACHMENTS

  Блок вложений для раскрытой канбан-карточки:
    • кнопка «Прикрепить файл» (multiple);
    • drag-and-drop любых файлов из ОС на зону;
    • вставка скриншотов из буфера обмена (Ctrl/Cmd+V) — ловим image/* из
      clipboardData.items, грузим как screenshot-YYYYMMDD-HHMMSS.png;
    • список миниатюр (картинки) + плиток с иконкой/именем/размером;
    • клик по имени / миниатюре — открытие в новой вкладке;
    • «удалить» — DELETE на сервере + чистка локального списка.
*/

import React, { useCallback, useRef, useState } from "react";
import {
  Download,
  File as FileIcon,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileType,
  Loader2,
  Mail,
  Paperclip,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import type { BacklogAttachment } from "@/store/projectStore";
import {
  deleteProjectFileOnServer,
  projectFileUrl,
  uploadBacklogAttachment,
} from "@/lib/api";
import { cn } from "@/lib/utils";

export interface BacklogAttachmentsProps {
  projectId: string;
  items: BacklogAttachment[];
  onChange: (next: BacklogAttachment[]) => void;
  readOnly?: boolean;
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0 Б";
  const units = ["Б", "КБ", "МБ", "ГБ"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const rounded = v >= 10 ? Math.round(v) : Math.round(v * 10) / 10;
  return `${rounded} ${units[i]}`;
}

function iconForMime(
  mime: string,
  name?: string,
): React.ComponentType<{ className?: string; strokeWidth?: number }> {
  if (mime.startsWith("image/")) return FileImage;
  if (mime === "application/pdf") return FileText;
  if (
    mime === "application/vnd.ms-excel" ||
    mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  )
    return FileSpreadsheet;
  if (
    mime === "application/msword" ||
    mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  )
    return FileType;
  if (mime === "text/plain") return FileText;
  // E-mail. Принимаем как стандартный mime, так и .eml-расширение —
  // на случай старых вложений, сохранённых с octet-stream.
  if (
    mime === "message/rfc822" ||
    mime === "application/eml" ||
    mime === "application/x-eml" ||
    (!!name && /\.eml$/i.test(name))
  ) {
    return Mail;
  }
  return FileIcon;
}

function tsScreenshotName(ext: string): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const s = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(
    d.getMinutes(),
  )}${pad(d.getSeconds())}`;
  return `screenshot-${s}.${ext}`;
}

export function BacklogAttachments({
  projectId,
  items,
  onChange,
  readOnly = false,
}: BacklogAttachmentsProps) {
  const [uploadingNames, setUploadingNames] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const itemsRef = useRef<BacklogAttachment[]>(items);
  itemsRef.current = items;

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (readOnly || files.length === 0) return;
      const names = files.map((f) => f.name || "file");
      setUploadingNames((prev) => [...prev, ...names]);
      const added: BacklogAttachment[] = [];
      for (const file of files) {
        try {
          const att = await uploadBacklogAttachment(projectId, file);
          added.push(att);
        } catch (e) {
          const msg = e instanceof Error ? e.message : "Ошибка загрузки";
          toast.error(`Не удалось загрузить «${file.name}»: ${msg}`);
        } finally {
          setUploadingNames((prev) => {
            const idx = prev.indexOf(file.name || "file");
            if (idx < 0) return prev;
            const next = prev.slice();
            next.splice(idx, 1);
            return next;
          });
        }
      }
      if (added.length > 0) {
        onChange([...(itemsRef.current ?? []), ...added]);
      }
    },
    [projectId, onChange, readOnly],
  );

  const onPickClick = () => {
    if (readOnly) return;
    inputRef.current?.click();
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list || list.length === 0) return;
    void uploadFiles(Array.from(list));
    e.target.value = "";
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (readOnly) return;
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) void uploadFiles(files);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    if (!dragActive) setDragActive(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    if (readOnly) return;
    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;
    const captured: File[] = [];
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      if (it.kind !== "file") continue;
      if (!it.type.startsWith("image/")) continue;
      const blob = it.getAsFile();
      if (!blob) continue;
      const ext = (blob.type.split("/")[1] || "png").replace(/[^a-z0-9]/gi, "") || "png";
      const renamed = new File([blob], tsScreenshotName(ext), {
        type: blob.type,
        lastModified: Date.now(),
      });
      captured.push(renamed);
    }
    if (captured.length > 0) {
      e.preventDefault();
      void uploadFiles(captured);
    }
  };

  const removeAttachment = async (att: BacklogAttachment) => {
    if (readOnly) return;
    if (typeof window !== "undefined") {
      const ok = window.confirm(`Удалить файл «${att.name}»? Действие необратимо.`);
      if (!ok) return;
    }
    setBusyId(att.id);
    try {
      await deleteProjectFileOnServer(projectId, att.id);
      onChange(itemsRef.current.filter((x) => x.id !== att.id));
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Ошибка удаления";
      toast.error(`Не удалось удалить «${att.name}»: ${msg}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className={cn(
        "group/attachments rounded-md border border-dashed px-2 py-2 transition-colors",
        dragActive
          ? "border-[var(--nn-blue)] bg-[var(--nn-blue-pale)]/30"
          : "border-border/70 bg-background",
      )}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={onDragLeave}
      onPaste={onPaste}
      tabIndex={readOnly ? -1 : 0}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
          <Paperclip className="h-3 w-3" /> Вложения
          {items.length > 0 ? (
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-semibold text-foreground">
              {items.length}
            </span>
          ) : null}
        </span>
        {!readOnly ? (
          <button
            type="button"
            onClick={onPickClick}
            className="inline-flex items-center gap-1 rounded-md border border-border/70 bg-background px-2 py-0.5 text-[11px] font-medium text-foreground transition-colors hover:border-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)]/30 hover:text-[var(--nn-blue)]"
          >
            <Upload className="h-3 w-3" /> Прикрепить
          </button>
        ) : null}
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          onChange={onInputChange}
          aria-label="Прикрепить файлы к карточке"
        />
      </div>

      {items.length === 0 && uploadingNames.length === 0 ? (
        <div className="py-3 text-center text-[11px] italic text-muted-foreground">
          {readOnly
            ? "Вложений нет"
            : "Перетащите сюда файлы, вставьте скриншот (Ctrl/Cmd + V) или нажмите «Прикрепить»"}
        </div>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {items.map((att) => {
            const isImage = att.mime.startsWith("image/");
            const Icon = iconForMime(att.mime, att.name);
            const url = projectFileUrl(projectId, att.id);
            const busy = busyId === att.id;
            return (
              <li
                key={att.id}
                className="flex items-center gap-2 rounded-md border border-border/60 bg-background px-2 py-1.5 text-xs"
              >
                {isImage ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="block h-9 w-9 shrink-0 overflow-hidden rounded border border-border/60 bg-muted"
                    title={att.name}
                  >
                    <img
                      src={url}
                      alt={att.name}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  </a>
                ) : (
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded border border-border/60 bg-muted text-muted-foreground">
                    <Icon className="h-4 w-4" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate text-xs font-medium text-foreground hover:text-[var(--nn-blue)] hover:underline"
                    title={att.name}
                  >
                    {att.name}
                  </a>
                  <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-muted-foreground">
                    <span>{formatBytes(att.size)}</span>
                    {att.uploadedByLogin ? (
                      <>
                        <span aria-hidden>·</span>
                        <span className="truncate">{att.uploadedByLogin}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={`Скачать ${att.name}`}
                  title="Скачать / открыть"
                >
                  <Download className="h-3.5 w-3.5" />
                </a>
                {!readOnly ? (
                  <button
                    type="button"
                    onClick={() => void removeAttachment(att)}
                    disabled={busy}
                    className="inline-flex h-6 w-6 items-center justify-center rounded text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-950/40"
                    aria-label={`Удалить ${att.name}`}
                    title="Удалить"
                  >
                    {busy ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </button>
                ) : null}
              </li>
            );
          })}
          {uploadingNames.map((name, idx) => (
            <li
              key={`up-${idx}-${name}`}
              className="flex items-center gap-2 rounded-md border border-dashed border-border/60 bg-muted/30 px-2 py-1.5 text-xs text-muted-foreground"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded border border-border/60 bg-muted">
                <Loader2 className="h-4 w-4 animate-spin" />
              </span>
              <span className="min-w-0 flex-1 truncate">{name}</span>
              <span className="text-[10px] italic">загружается…</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
